import { useEffect, useMemo, useRef, useState } from "react";
import {
  api,
  type DispatchAgent,
  type Task,
  type TaskAgent,
  type TaskFeedback,
  type TaskSeverity,
  type TaskStage,
} from "../api/client";
import { wsClient } from "../api/ws";

function getApiBase(): string {
  return (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";
}

function getAuthToken(): string | null {
  if (typeof localStorage === "undefined") return null;
  return localStorage.getItem("coagent_console_token");
}

// ============================================================================
// Stage / pipeline metadata
// ============================================================================

const STAGE_COLUMNS: Array<{ id: TaskStage; label: string; icon: string }> = [
  { id: "inbox", label: "Новые", icon: "📥" },
  { id: "triage", label: "Анализ", icon: "🔍" },
  { id: "in_progress", label: "В работе", icon: "💻" },
  { id: "review", label: "Ревью", icon: "👀" },
  { id: "deploy", label: "Деплой", icon: "🚀" },
  { id: "done", label: "Готово", icon: "✅" },
];

const ALL_STAGES: TaskStage[] = [
  "inbox",
  "triage",
  "in_progress",
  "review",
  "deploy",
  "done",
  "blocked",
];

const STAGE_LABEL: Record<TaskStage, string> = {
  inbox: "📥 Новые",
  triage: "🔍 Анализ",
  in_progress: "💻 В работе",
  review: "👀 Ревью",
  deploy: "🚀 Деплой",
  done: "✅ Готово",
  blocked: "🚧 Заблокировано",
};

// Fallback: when backend task has no `stage` yet, derive a sensible default
// from legacy `status` so all tasks land somewhere visible.
function resolveStage(t: Task): TaskStage {
  if (t.stage) return t.stage;
  switch (t.status) {
    case "todo":
      return "inbox";
    case "in_progress":
      return "in_progress";
    case "review":
      return "review";
    case "done":
      return "done";
    default:
      return "inbox";
  }
}

const SEVERITY_PILL: Record<TaskSeverity, { label: string; cls: string }> = {
  green: { label: "🟢 GREEN", cls: "border-panel-ok text-panel-ok" },
  yellow: { label: "🟡 YELLOW", cls: "border-panel-warn text-panel-warn" },
  red: { label: "🔴 RED", cls: "border-panel-err text-panel-err" },
};

const AGENT_PILL: Record<NonNullable<TaskAgent>, string> = {
  claude: "bg-violet-500/20 text-violet-300 border-violet-500/40",
  codex: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  qwen: "bg-sky-500/20 text-sky-300 border-sky-500/40",
  auto: "bg-slate-500/20 text-slate-300 border-slate-500/40",
};

// Phase 8b — escalation metadata shape written by backend escalation guard.
type EscalationMeta = {
  reason?: string;
  at?: string;
  by?: string;
  diagnostic?: Record<string, unknown>;
};

function getEscalation(task: Task): EscalationMeta | null {
  const m = (task.metadata ?? {}) as Record<string, unknown>;
  const e = m["escalation"];
  if (!e || typeof e !== "object") return null;
  return e as EscalationMeta;
}

function isAttentionTask(task: Task): boolean {
  const m = (task.metadata ?? {}) as Record<string, unknown>;
  return m["swim_lane"] === "attention" || !!getEscalation(task);
}

const SOURCE_BADGE: Record<Task["source"], string> = {
  manual: "bg-panel-border",
  autopilot: "bg-panel-accent text-black",
  logwatch: "bg-panel-warn text-black",
  signal: "bg-panel-err text-black",
  tg: "bg-panel-ok text-black",
  pr: "bg-panel-border",
  imported: "bg-panel-border text-panel-muted",
};

// ============================================================================
// Inbox grouping (by source)
// ============================================================================

type InboxGroup = "signals" | "autopilot" | "tg" | "other";

function classifyInboxSource(source: string): InboxGroup {
  if (source === "signal-prod" || source === "signal-stage" || source === "signal") return "signals";
  if (source === "autopilot" || source === "logwatch") return "autopilot";
  if (source === "tg") return "tg";
  return "other";
}

const GROUP_CONFIG: Record<InboxGroup, { icon: string; label: string; order: number }> = {
  signals: { icon: "🔴", label: "Баги и сигналы", order: 1 },
  autopilot: { icon: "🔵", label: "Автопилот", order: 2 },
  tg: { icon: "🟢", label: "Из Telegram", order: 3 },
  other: { icon: "🟡", label: "Мои и импорт", order: 4 },
};

const INBOX_GROUP_ORDER: InboxGroup[] = (["signals", "autopilot", "tg", "other"] as InboxGroup[]).sort(
  (a, b) => GROUP_CONFIG[a].order - GROUP_CONFIG[b].order,
);

// ============================================================================
// Toast (very small, ephemeral)
// ============================================================================

type Toast = { id: string; kind: "ok" | "err" | "info"; text: string; action?: { label: string; href: string } };

// ============================================================================
// Main page
// ============================================================================

export function Tasks() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [stageCounts, setStageCounts] = useState<Record<TaskStage, number> | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [solveTask, setSolveTask] = useState<Task | null>(null);
  const [lastUpdate, setLastUpdate] = useState<number>(Date.now());
  const [blockedCollapsed, setBlockedCollapsed] = useState<boolean>(true);
  const [dispatchAgent, setDispatchAgent] = useState<DispatchAgent>("auto");
  const [busy, setBusy] = useState<boolean>(false);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [nowTick, setNowTick] = useState<number>(Date.now());

  // filters
  const [filterSource, setFilterSource] = useState<"all" | Task["source"]>("all");
  const [filterSeverity, setFilterSeverity] = useState<"all" | TaskSeverity>("all");
  const [filterAgent, setFilterAgent] = useState<"all" | "none" | NonNullable<TaskAgent>>("all");
  const [search, setSearch] = useState<string>("");

  const reload = async () => {
    try {
      const [list, summary] = await Promise.all([
        api.tasks.list({ limit: "500" }),
        api.tasks.summaryByStage().catch(() => ({ summary: [] as Array<{ stage: string; count: number }> })),
      ]);
      setTasks(list.tasks);
      const counts: Record<TaskStage, number> = {
        inbox: 0,
        triage: 0,
        in_progress: 0,
        review: 0,
        deploy: 0,
        done: 0,
        blocked: 0,
      };
      for (const row of summary.summary) {
        if ((ALL_STAGES as string[]).includes(row.stage)) counts[row.stage as TaskStage] = row.count;
      }
      setStageCounts(counts);
      setErr(null);
      setLastUpdate(Date.now());
    } catch (e) {
      setErr(String(e));
    }
  };

  useEffect(() => {
    reload();
    const off = wsClient.subscribe((e) => {
      if (e.type === "task.update") reload();
    });
    const poll = window.setInterval(reload, 10_000);
    const tickTimer = window.setInterval(() => setNowTick(Date.now()), 1000);
    return () => {
      off();
      window.clearInterval(poll);
      window.clearInterval(tickTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pushToast = (t: Omit<Toast, "id">) => {
    const id = `t-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    setToasts((prev) => [...prev, { ...t, id }]);
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((x) => x.id !== id));
    }, 8000);
  };

  // ----- filtering -----
  const filteredTasks = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tasks.filter((t) => {
      if (filterSource !== "all" && t.source !== filterSource) return false;
      if (filterSeverity !== "all" && (t.severity ?? null) !== filterSeverity) return false;
      if (filterAgent !== "all") {
        if (filterAgent === "none") {
          if (t.agent_assigned) return false;
        } else if (t.agent_assigned !== filterAgent) {
          return false;
        }
      }
      if (q) {
        const hay = (t.title + " " + (t.description ?? "")).toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [tasks, filterSource, filterSeverity, filterAgent, search]);

  const tasksByStage = useMemo(() => {
    const map: Record<TaskStage, Task[]> = {
      inbox: [],
      triage: [],
      in_progress: [],
      review: [],
      deploy: [],
      done: [],
      blocked: [],
    };
    for (const t of filteredTasks) map[resolveStage(t)].push(t);
    return map;
  }, [filteredTasks]);

  // ----- selection -----
  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectStage = (stage: TaskStage) => {
    const ids = tasksByStage[stage].map((t) => t.id);
    const allSelected = ids.length > 0 && ids.every((id) => selected.has(id));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  // ----- mutations -----
  const moveTaskStage = async (id: string, stage: TaskStage) => {
    try {
      await api.tasks.update(id, { stage });
      reload();
    } catch (e) {
      setErr(String(e));
    }
  };

  const bulkStage = async (stage: TaskStage) => {
    if (selected.size === 0 || busy) return;
    setBusy(true);
    try {
      const ids = Array.from(selected);
      await api.tasks.bulkStage(ids, stage);
      pushToast({ kind: "ok", text: `Перемещено ${ids.length} задач в «${STAGE_LABEL[stage]}»` });
      clearSelection();
      reload();
    } catch (e) {
      setErr(String(e));
      pushToast({ kind: "err", text: `Ошибка: ${String(e).slice(0, 120)}` });
    } finally {
      setBusy(false);
    }
  };

  const dispatchSelected = async () => {
    if (selected.size === 0 || busy) return;
    setBusy(true);
    try {
      const ids = Array.from(selected);
      const res = await api.tasks.dispatch(ids, dispatchAgent, "ide");
      const agentLabel = dispatchAgent === "auto" ? "auto" : dispatchAgent;
      pushToast({
        kind: "ok",
        text: `Отправлено ${res.dispatched} задач агенту ${agentLabel}. Открыть IDE?`,
        action: { label: "Открыть IDE", href: "https://ide.co-agent.ru/?folder=/workspace" },
      });
      if (res.skipped && res.skipped.length > 0) {
        pushToast({ kind: "info", text: `Пропущено: ${res.skipped.length}` });
      }
      clearSelection();
      reload();
    } catch (e) {
      setErr(String(e));
      pushToast({ kind: "err", text: `Dispatch failed: ${String(e).slice(0, 120)}` });
    } finally {
      setBusy(false);
    }
  };

  const deleteTask = async (id: string) => {
    if (!confirm("Удалить задачу?")) return;
    try {
      await api.tasks.remove(id);
      reload();
    } catch (e) {
      setErr(String(e));
    }
  };

  const createTask = async () => {
    if (!newTitle.trim()) return;
    try {
      await api.tasks.create({ title: newTitle.trim(), source: "manual" });
      setNewTitle("");
      reload();
    } catch (e) {
      setErr(String(e));
    }
  };

  const secsAgo = Math.max(0, Math.floor((nowTick - lastUpdate) / 1000));

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div className="space-y-4 pb-24">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Задачи · Kanban</h1>
          <p className="text-sm text-panel-muted">
            {tasks.length} всего · 6-стадийный пайплайн · обновлено {secsAgo} сек назад
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={reload}
            className="btn text-sm"
            title="Обновить сейчас"
          >
            🔄 Обновить
          </button>
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              createTask();
            }}
          >
            <input
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="Новая задача…"
              className="rounded border border-panel-border bg-panel-bg px-3 py-1.5 text-sm"
            />
            <button className="btn btn-primary">Добавить</button>
          </form>
        </div>
      </header>

      {/* Filters */}
      <div className="card flex flex-wrap items-center gap-3 py-2">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="🔎 Поиск по тексту…"
          className="rounded border border-panel-border bg-panel-bg px-3 py-1.5 text-sm flex-1 min-w-[200px]"
        />
        <FilterSelect
          label="Источник"
          value={filterSource}
          onChange={(v) => setFilterSource(v as typeof filterSource)}
          options={[
            ["all", "Все"],
            ["manual", "Manual"],
            ["autopilot", "Autopilot"],
            ["signal", "Signal"],
            ["tg", "TG"],
            ["logwatch", "Logwatch"],
            ["pr", "PR"],
            ["imported", "Imported"],
          ]}
        />
        <FilterSelect
          label="Severity"
          value={filterSeverity}
          onChange={(v) => setFilterSeverity(v as typeof filterSeverity)}
          options={[
            ["all", "Все"],
            ["green", "🟢 Green"],
            ["yellow", "🟡 Yellow"],
            ["red", "🔴 Red"],
          ]}
        />
        <FilterSelect
          label="Агент"
          value={filterAgent}
          onChange={(v) => setFilterAgent(v as typeof filterAgent)}
          options={[
            ["all", "Все"],
            ["claude", "Claude"],
            ["codex", "Codex"],
            ["qwen", "Qwen"],
            ["auto", "Auto"],
            ["none", "Без агента"],
          ]}
        />
        {(search || filterSource !== "all" || filterSeverity !== "all" || filterAgent !== "all") && (
          <button
            onClick={() => {
              setSearch("");
              setFilterSource("all");
              setFilterSeverity("all");
              setFilterAgent("all");
            }}
            className="btn text-xs"
          >
            ✕ Сбросить фильтры
          </button>
        )}
      </div>

      {err && <div className="card pill-err border-panel-err">Ошибка: {err}</div>}

      {solveTask && (
        <SolveTaskModal
          task={solveTask}
          onClose={() => {
            setSolveTask(null);
            reload();
          }}
        />
      )}

      {/* Main 6-column grid. Horizontal scroll on narrow screens. */}
      <div className="overflow-x-auto">
        <div
          className="grid gap-3"
          style={{
            gridTemplateColumns: `repeat(${STAGE_COLUMNS.length}, minmax(280px, 1fr))`,
          }}
        >
          {STAGE_COLUMNS.map((col) => {
            const items = tasksByStage[col.id];
            const totalCount = stageCounts?.[col.id] ?? items.length;
            const colSelected = items.filter((t) => selected.has(t.id)).length;
            const allSel = items.length > 0 && colSelected === items.length;
            return (
              <div key={col.id} className="card min-h-[300px] flex flex-col">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <label className="flex items-center gap-2 text-sm font-medium">
                    <input
                      type="checkbox"
                      checked={allSel}
                      ref={(el) => {
                        if (el) el.indeterminate = colSelected > 0 && !allSel;
                      }}
                      onChange={() => selectStage(col.id)}
                      className="accent-panel-accent"
                      disabled={items.length === 0}
                    />
                    <span>
                      {col.icon} {col.label}
                    </span>
                  </label>
                  <span className="rounded-full bg-panel-border px-2 py-0.5 text-xs text-panel-fg">
                    {items.length}
                    {stageCounts && totalCount !== items.length && (
                      <span className="text-panel-muted"> / {totalCount}</span>
                    )}
                  </span>
                </div>
                {col.id === "inbox" ? (
                  <div className="flex-1 space-y-3">
                    {items.length === 0 && <div className="text-xs text-panel-muted">Пусто</div>}
                    {INBOX_GROUP_ORDER.map((g) => {
                      const groupItems = items.filter((t) => classifyInboxSource(t.source) === g);
                      if (groupItems.length === 0) return null;
                      return (
                        <InboxGroupSection
                          key={g}
                          group={g}
                          items={groupItems}
                          selected={selected}
                          onToggle={toggle}
                          onMove={moveTaskStage}
                          onSolve={(t) => setSolveTask(t)}
                          onDelete={deleteTask}
                        />
                      );
                    })}
                  </div>
                ) : (
                  <ul className="space-y-2 flex-1">
                    {items.map((t) => (
                      <TaskCard
                        key={t.id}
                        task={t}
                        selected={selected.has(t.id)}
                        onToggle={() => toggle(t.id)}
                        onMove={(s) => moveTaskStage(t.id, s)}
                        onSolve={() => setSolveTask(t)}
                        onDelete={() => deleteTask(t.id)}
                      />
                    ))}
                    {items.length === 0 && <li className="text-xs text-panel-muted">Пусто</li>}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Blocked swim-lane */}
      <BlockedLane
        tasks={tasksByStage.blocked}
        totalCount={stageCounts?.blocked ?? tasksByStage.blocked.length}
        collapsed={blockedCollapsed}
        onToggle={() => setBlockedCollapsed((v) => !v)}
        selected={selected}
        onToggleSelect={toggle}
        onMove={moveTaskStage}
        onSolve={(t) => setSolveTask(t)}
        onDelete={deleteTask}
      />

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <BulkActionBar
          count={selected.size}
          agent={dispatchAgent}
          busy={busy}
          onAgentChange={setDispatchAgent}
          onDispatch={dispatchSelected}
          onStageChange={bulkStage}
          onClear={clearSelection}
        />
      )}

      {/* Toasts */}
      <div className="fixed right-4 top-4 z-50 flex flex-col gap-2">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={
              "max-w-sm rounded-md border px-3 py-2 text-sm shadow-lg " +
              (t.kind === "ok"
                ? "border-panel-ok bg-panel-card text-panel-ok"
                : t.kind === "err"
                  ? "border-panel-err bg-panel-card text-panel-err"
                  : "border-panel-border bg-panel-card text-panel-fg")
            }
          >
            <div>{t.text}</div>
            {t.action && (
              <a
                href={t.action.href}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1 inline-block text-xs underline hover:opacity-80"
              >
                → {t.action.label}
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// FilterSelect
// ============================================================================

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<[string, string]>;
}) {
  return (
    <label className="flex items-center gap-1 text-xs text-panel-muted">
      {label}:
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded border border-panel-border bg-panel-bg px-2 py-1 text-xs text-panel-fg"
      >
        {options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    </label>
  );
}

// ============================================================================
// InboxGroupSection — collapsible sub-group inside the Inbox column
// ============================================================================

function InboxGroupSection({
  group,
  items,
  selected,
  onToggle,
  onMove,
  onSolve,
  onDelete,
}: {
  group: InboxGroup;
  items: Task[];
  selected: Set<string>;
  onToggle: (id: string) => void;
  onMove: (id: string, stage: TaskStage) => void;
  onSolve: (t: Task) => void;
  onDelete: (id: string) => void;
}) {
  const cfg = GROUP_CONFIG[group];
  // Default expand/collapse depends on group size at first render.
  const [collapsed, setCollapsed] = useState<boolean>(items.length > 10);
  const [expanded, setExpanded] = useState<boolean>(false);

  const sorted = useMemo(() => {
    return [...items].sort((a, b) => {
      const ta = a.created_at ? Date.parse(a.created_at) : 0;
      const tb = b.created_at ? Date.parse(b.created_at) : 0;
      return tb - ta;
    });
  }, [items]);

  const VISIBLE = 10;
  const visible = expanded ? sorted : sorted.slice(0, VISIBLE);
  const hiddenCount = sorted.length - visible.length;

  return (
    <div className="rounded border border-panel-border bg-panel-card/40">
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className="flex w-full items-center justify-between gap-2 px-2 py-1.5 text-left"
        title={collapsed ? "Развернуть" : "Свернуть"}
      >
        <span className="flex items-center gap-2 text-xs font-medium">
          <span>{cfg.icon}</span>
          <span>{cfg.label}</span>
          <span className="rounded-full bg-panel-border px-1.5 py-0.5 text-[10px] text-panel-fg">
            {items.length}
          </span>
        </span>
        <span className="text-[10px] text-panel-muted">{collapsed ? "▾" : "▴"}</span>
      </button>
      {!collapsed && (
        <ul className="space-y-2 px-1.5 pb-2">
          {visible.map((t) => (
            <TaskCard
              key={t.id}
              task={t}
              selected={selected.has(t.id)}
              onToggle={() => onToggle(t.id)}
              onMove={(s) => onMove(t.id, s)}
              onSolve={() => onSolve(t)}
              onDelete={() => onDelete(t.id)}
            />
          ))}
          {hiddenCount > 0 && (
            <li>
              <button
                type="button"
                onClick={() => setExpanded(true)}
                className="w-full rounded border border-dashed border-panel-border px-2 py-1 text-[11px] text-panel-muted hover:bg-panel-border hover:text-panel-fg"
              >
                показать ещё {hiddenCount} ▾
              </button>
            </li>
          )}
          {expanded && sorted.length > VISIBLE && (
            <li>
              <button
                type="button"
                onClick={() => setExpanded(false)}
                className="w-full rounded border border-dashed border-panel-border px-2 py-1 text-[11px] text-panel-muted hover:bg-panel-border hover:text-panel-fg"
              >
                свернуть ▴
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

// ============================================================================
// TaskCard
// ============================================================================

function TaskCard({
  task,
  selected,
  onToggle,
  onMove,
  onSolve,
  onDelete,
}: {
  task: Task;
  selected: boolean;
  onToggle: () => void;
  onMove: (stage: TaskStage) => void;
  onSolve: () => void;
  onDelete: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const stage = resolveStage(task);

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  const sev = task.severity ?? null;
  const agent = task.agent_assigned ?? null;
  const costDollars = task.cost_cents_spent ? (task.cost_cents_spent / 100).toFixed(2) : null;
  const escalation = getEscalation(task);

  return (
    <li
      className={
        "rounded border p-2 text-sm transition " +
        (selected ? "border-panel-accent bg-panel-accent/10" : "border-panel-border bg-panel-bg")
      }
    >
      <div className="flex items-start gap-2">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          className="mt-0.5 shrink-0 accent-panel-accent"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="font-medium text-panel-fg break-words">{task.title}</div>
            <div className="relative shrink-0" ref={menuRef}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpen((v) => !v);
                }}
                className="rounded px-1 text-panel-muted hover:bg-panel-border hover:text-panel-fg"
                title="Меню"
              >
                ⋮
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-6 z-30 w-48 rounded-md border border-panel-border bg-panel-card py-1 shadow-lg">
                  <div className="border-b border-panel-border px-3 py-1 text-[10px] uppercase text-panel-muted">
                    Переместить в…
                  </div>
                  {ALL_STAGES.filter((s) => s !== stage).map((s) => (
                    <button
                      key={s}
                      onClick={() => {
                        setMenuOpen(false);
                        onMove(s);
                      }}
                      className="block w-full px-3 py-1 text-left text-xs hover:bg-panel-border"
                    >
                      → {STAGE_LABEL[s]}
                    </button>
                  ))}
                  <div className="mt-1 border-t border-panel-border pt-1">
                    <button
                      onClick={() => {
                        setMenuOpen(false);
                        onDelete();
                      }}
                      className="block w-full px-3 py-1 text-left text-xs text-panel-err hover:bg-panel-err hover:text-black"
                    >
                      🗑 Удалить
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {task.description && (
            <div className="mt-1 line-clamp-2 text-xs text-panel-muted break-words">
              {task.description}
            </div>
          )}

          <div className="mt-2 flex flex-wrap items-center gap-1">
            {escalation && (
              <span
                className="pill text-[10px] border-panel-warn text-panel-warn bg-panel-warn/10"
                title={
                  `Причина: ${escalation.reason ?? "?"}` +
                  (escalation.at ? `\nВ: ${escalation.at}` : "") +
                  (escalation.by ? `\nКто: ${escalation.by}` : "") +
                  (escalation.diagnostic
                    ? `\nДиагностика: ${JSON.stringify(escalation.diagnostic).slice(0, 400)}`
                    : "")
                }
              >
                ⚠️ Требует внимания
              </span>
            )}
            {sev && (
              <span className={`pill text-[10px] ${SEVERITY_PILL[sev].cls}`}>
                {SEVERITY_PILL[sev].label}
              </span>
            )}
            {agent && (
              <span className={`pill text-[10px] ${AGENT_PILL[agent]}`}>{agent}</span>
            )}
            <span className={`pill text-[10px] ${SOURCE_BADGE[task.source] ?? ""}`}>
              {task.source}
            </span>
            {costDollars && (
              <span
                className="pill text-[10px] border-panel-warn text-panel-warn"
                title={`Потрачено: $${costDollars}`}
              >
                💰 ${costDollars}
              </span>
            )}
            {task.related_pr_url && (
              <a
                href={task.related_pr_url}
                target="_blank"
                rel="noopener noreferrer"
                className="pill text-[10px] border-panel-accent text-panel-accent hover:bg-panel-accent hover:text-black"
                title={task.related_pr_url}
                onClick={(e) => e.stopPropagation()}
              >
                🔗 PR
              </a>
            )}
            {task.consensus && sev === "green" && (
              <span
                className={
                  "pill text-[10px] " +
                  (task.consensus.decision === "APPROVE"
                    ? "border-panel-ok text-panel-ok"
                    : task.consensus.decision === "DEMAND_CANARY"
                    ? "border-panel-warn text-panel-warn"
                    : "border-panel-err text-panel-err")
                }
                title={
                  `Verifier: ${task.consensus.verifier_agent ?? "?"}\n` +
                  (task.consensus.verifier_notes ?? "")
                }
              >
                {task.consensus.decision === "APPROVE"
                  ? "✓ consensus"
                  : task.consensus.decision === "DEMAND_CANARY"
                  ? "⚠ demand-canary"
                  : "✗ rejected"}
              </span>
            )}
          </div>

          <div className="mt-2 flex flex-wrap gap-1">
            {(stage === "inbox" || stage === "triage") && (
              <button
                onClick={onSolve}
                className="rounded border border-panel-accent bg-panel-accent/10 px-1.5 py-0.5 text-[10px] text-panel-accent hover:bg-panel-accent hover:text-black"
                title="Запустить Claude на эту задачу"
              >
                🤖 Решить
              </button>
            )}
            {stage === "done" && <FeedbackButtons task={task} />}
          </div>
        </div>
      </div>
    </li>
  );
}

// ============================================================================
// FeedbackButtons — 👍 / 👎 on every done-column card (Phase 8g).
// Feeds the outcomes ledger via POST /api/tasks/:id/feedback.
// ============================================================================

function FeedbackButtons({ task }: { task: Task }) {
  const existing = (task.metadata?.feedback ?? null) as TaskFeedback | null;
  const [busy, setBusy] = useState(false);
  const [local, setLocal] = useState<TaskFeedback | null>(existing);

  useEffect(() => {
    setLocal((task.metadata?.feedback ?? null) as TaskFeedback | null);
  }, [task.metadata]);

  if (local) {
    const cls =
      local.value === "positive"
        ? "border-panel-ok text-panel-ok"
        : local.value === "negative"
          ? "border-panel-err text-panel-err"
          : "border-panel-border text-panel-muted";
    return (
      <span
        className={`pill text-[10px] ${cls}`}
        title={local.note ? `Заметка: ${local.note}` : ""}
      >
        {local.value === "positive"
          ? "✓ Работает"
          : local.value === "negative"
            ? "✗ Не работает"
            : "— Нейтрально"}
      </span>
    );
  }

  const send = async (value: "positive" | "negative") => {
    if (busy) return;
    setBusy(true);
    try {
      let note: string | undefined;
      if (value === "negative") {
        const reason = window.prompt("Что именно не работает? Короткая причина:");
        if (reason == null) {
          setBusy(false);
          return; // user canceled
        }
        note = reason.trim() || undefined;
      }
      const res = await api.tasks.feedback(task.id, value, note);
      const next = (res.task.metadata?.feedback ?? null) as TaskFeedback | null;
      setLocal(
        next ?? {
          value,
          note: note ?? null,
          at: new Date().toISOString(),
        },
      );
    } catch (e) {
      alert("Не удалось сохранить: " + String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => send("positive")}
        disabled={busy}
        className="rounded border border-panel-ok bg-panel-ok/10 px-1.5 py-0.5 text-[10px] text-panel-ok hover:bg-panel-ok hover:text-black disabled:opacity-50"
        title="Работает как ожидалось"
      >
        👍 Работает
      </button>
      <button
        onClick={() => send("negative")}
        disabled={busy}
        className="rounded border border-panel-err bg-panel-err/10 px-1.5 py-0.5 text-[10px] text-panel-err hover:bg-panel-err hover:text-black disabled:opacity-50"
        title="Не работает — опишите причину"
      >
        👎 Не работает
      </button>
    </>
  );
}

// ============================================================================
// Blocked swim-lane
// ============================================================================

function BlockedLane({
  tasks,
  totalCount,
  collapsed,
  onToggle,
  selected,
  onToggleSelect,
  onMove,
  onSolve,
  onDelete,
}: {
  tasks: Task[];
  totalCount: number;
  collapsed: boolean;
  onToggle: () => void;
  selected: Set<string>;
  onToggleSelect: (id: string) => void;
  onMove: (id: string, stage: TaskStage) => void;
  onSolve: (t: Task) => void;
  onDelete: (id: string) => void;
}) {
  // Phase 8b — split blocked into attention (auto-escalated) and ordinary.
  const attention = tasks.filter((t) => isAttentionTask(t));
  const ordinary = tasks.filter((t) => !isAttentionTask(t));

  return (
    <div className="space-y-3">
      {/* ATTENTION swim-lane: tasks auto-escalated by budget/reviewer/etc. */}
      {attention.length > 0 && (
        <div className="card border-panel-err bg-panel-err/5">
          <div className="flex w-full items-center justify-between gap-2">
            <div className="flex items-center gap-2 font-medium text-panel-err">
              <span>⚠️ Требуют внимания</span>
              <span className="rounded-full bg-panel-err px-2 py-0.5 text-xs text-black">
                {attention.length}
              </span>
              <span className="text-xs text-panel-muted">
                (автоэскалация — превышен бюджет, блокирующий reviewer или ошибка)
              </span>
            </div>
          </div>
          <div className="mt-3 overflow-x-auto">
            <ul className="flex gap-2 min-w-max pb-1">
              {attention.map((t) => (
                <li key={t.id} className="w-[280px] shrink-0">
                  <TaskCard
                    task={t}
                    selected={selected.has(t.id)}
                    onToggle={() => onToggleSelect(t.id)}
                    onMove={(s) => onMove(t.id, s)}
                    onSolve={() => onSolve(t)}
                    onDelete={() => onDelete(t.id)}
                  />
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Ordinary blocked lane (no escalation). */}
      <div className="card border-panel-warn">
        <button
          onClick={onToggle}
          className="flex w-full items-center justify-between gap-2 text-left"
        >
          <div className="flex items-center gap-2 font-medium">
            <span>🚧 Заблокировано</span>
            <span className="rounded-full bg-panel-border px-2 py-0.5 text-xs text-panel-fg">
              {ordinary.length}
              {totalCount !== tasks.length && <span className="text-panel-muted"> / {totalCount}</span>}
            </span>
          </div>
          <span className="text-panel-muted text-sm">{collapsed ? "▾ Показать" : "▴ Скрыть"}</span>
        </button>
        {!collapsed && (
          <div className="mt-3 overflow-x-auto">
            <ul className="flex gap-2 min-w-max pb-1">
              {ordinary.length === 0 && (
                <li className="text-xs text-panel-muted">Заблокированных задач нет.</li>
              )}
              {ordinary.map((t) => (
                <li key={t.id} className="w-[280px] shrink-0">
                  <TaskCard
                    task={t}
                    selected={selected.has(t.id)}
                    onToggle={() => onToggleSelect(t.id)}
                    onMove={(s) => onMove(t.id, s)}
                    onSolve={() => onSolve(t)}
                    onDelete={() => onDelete(t.id)}
                  />
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// BulkActionBar — sticky bottom bar when selection is non-empty
// ============================================================================

function BulkActionBar({
  count,
  agent,
  busy,
  onAgentChange,
  onDispatch,
  onStageChange,
  onClear,
}: {
  count: number;
  agent: DispatchAgent;
  busy: boolean;
  onAgentChange: (a: DispatchAgent) => void;
  onDispatch: () => void;
  onStageChange: (s: TaskStage) => void;
  onClear: () => void;
}) {
  return (
    <div className="fixed inset-x-0 bottom-0 z-40 border-t border-panel-border bg-panel-card/95 backdrop-blur px-4 py-3 shadow-lg">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-3">
        <div className="text-sm font-medium">
          Выбрано: <span className="text-panel-accent">{count}</span>
        </div>

        <label className="flex items-center gap-1 text-xs text-panel-muted">
          Агент:
          <select
            value={agent}
            onChange={(e) => onAgentChange(e.target.value as DispatchAgent)}
            className="rounded border border-panel-border bg-panel-bg px-2 py-1 text-xs text-panel-fg"
            disabled={busy}
          >
            <option value="auto">Auto</option>
            <option value="claude">Claude</option>
            <option value="codex">Codex</option>
          </select>
        </label>

        <button
          onClick={onDispatch}
          disabled={busy}
          className="btn btn-primary text-sm disabled:opacity-50"
          title="Отправить задачи в очередь IDE"
        >
          {busy ? "…" : "🚀 В IDE"}
        </button>

        <label className="flex items-center gap-1 text-xs text-panel-muted">
          Стадия:
          <select
            onChange={(e) => {
              const v = e.target.value as TaskStage | "";
              if (v) {
                onStageChange(v);
                e.target.value = "";
              }
            }}
            defaultValue=""
            className="rounded border border-panel-border bg-panel-bg px-2 py-1 text-xs text-panel-fg"
            disabled={busy}
          >
            <option value="" disabled>
              Переместить…
            </option>
            {ALL_STAGES.map((s) => (
              <option key={s} value={s}>
                {STAGE_LABEL[s]}
              </option>
            ))}
          </select>
        </label>

        <button
          onClick={onClear}
          className="btn text-sm ml-auto"
          disabled={busy}
        >
          ✕ Сбросить
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// SolveTaskModal — opens SSE to /api/tasks/:id/solve and renders live stream.
// (Carried over verbatim from previous MVP.)
// ============================================================================

interface SolveLogEntry {
  id: string;
  kind: string;
  text: string;
  ts: number;
}

function SolveTaskModal({ task, onClose }: { task: Task; onClose: () => void }) {
  const [log, setLog] = useState<SolveLogEntry[]>([]);
  const [status, setStatus] = useState<"running" | "done" | "error" | "aborted">("running");
  const [finalText, setFinalText] = useState<string>("");
  const [reason, setReason] = useState<string>("");
  const abortRef = useRef<AbortController | null>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const startedRef = useRef(false);

  const append = (kind: string, text: string) => {
    setLog((prev) => [
      ...prev,
      { id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, kind, text, ts: Date.now() },
    ]);
  };

  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight });
  }, [log.length]);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const ctrl = new AbortController();
    abortRef.current = ctrl;

    const parseSSE = (
      chunk: string,
      onEvent: (type: string, data: Record<string, unknown>) => void,
      state: { buf: string },
    ) => {
      state.buf += chunk;
      const blocks = state.buf.split(/\r?\n\r?\n/);
      state.buf = blocks.pop() ?? "";
      for (const block of blocks) {
        const lines = block.split(/\r?\n/);
        let eventType = "message";
        let dataStr = "";
        for (const ln of lines) {
          const line = ln.trimEnd();
          if (!line) continue;
          if (line.startsWith("event:")) {
            eventType = line.slice(6).trim();
          } else if (line.startsWith("data:")) {
            const piece = line.slice(5).replace(/^\s/, "");
            dataStr += (dataStr ? "\n" : "") + piece;
          }
        }
        if (!dataStr) continue;
        try {
          const data = JSON.parse(dataStr);
          onEvent(eventType, data);
        } catch (err) {
          console.warn("[solve-sse] parse failed", err, "raw:", dataStr.slice(0, 200));
        }
      }
    };

    (async () => {
      try {
        const token = getAuthToken();
        const res = await fetch(`${getApiBase()}/tasks/${task.id}/solve`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: "{}",
          signal: ctrl.signal,
        });

        if (!res.ok || !res.body) {
          const body = await res.text().catch(() => "");
          append("error", `HTTP ${res.status}: ${body.slice(0, 200)}`);
          setStatus("error");
          return;
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        const state = { buf: "" };

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          const chunk = decoder.decode(value, { stream: true });

          parseSSE(chunk, (type, data) => {
            if (type === "start") {
              append("system", `Запуск решения задачи: ${String((data as { title?: string }).title ?? "")}`);
            } else if (type === "warn") {
              append("warn", String((data as { text?: string }).text ?? ""));
            } else if (type === "claude") {
              const ev = data as {
                kind?: string;
                text?: string;
                tool?: string;
                input?: unknown;
                content?: unknown;
                model?: string;
                tools?: string[];
              };
              const kind = ev.kind ?? "unknown";
              if (kind === "text") {
                append("text", String(ev.text ?? ""));
              } else if (kind === "thinking") {
                append("thinking", String(ev.text ?? ""));
              } else if (kind === "tool_use") {
                const input = ev.input ? " " + JSON.stringify(ev.input).slice(0, 200) : "";
                append("tool_use", `🔧 ${ev.tool}${input}`);
              } else if (kind === "tool_result") {
                const contentStr =
                  typeof ev.content === "string"
                    ? ev.content
                    : JSON.stringify(ev.content).slice(0, 300);
                append("tool_result", `✅ ${contentStr}`);
              } else if (kind === "system") {
                append(
                  "system",
                  `model: ${ev.model ?? "?"} · tools: ${(ev.tools ?? []).length}`,
                );
              } else if (kind === "result") {
                // final "result" event — text captured via onEnd too, skip duplicate
              } else if (kind === "error") {
                append("error", String(ev.text ?? "unknown error"));
              }
            } else if (type === "done") {
              const d = data as { reason?: string; final?: string; ok?: boolean };
              setFinalText(d.final ?? "");
              setReason(d.reason ?? "");
              setStatus(d.ok ? "done" : "error");
            }
          }, state);
        }
      } catch (err) {
        if ((err as Error).name === "AbortError") {
          setStatus("aborted");
          append("system", "Прервано пользователем.");
        } else {
          append("error", `Сетевая ошибка: ${String(err)}`);
          setStatus("error");
        }
      } finally {
        abortRef.current = null;
      }
    })();

    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.id]);

  const stop = () => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  };

  const entryClass = (kind: string): string => {
    switch (kind) {
      case "text":
        return "text-panel-fg";
      case "thinking":
        return "text-panel-muted italic";
      case "tool_use":
        return "text-sky-400";
      case "tool_result":
        return "text-emerald-400";
      case "system":
        return "text-panel-muted";
      case "warn":
        return "text-panel-warn";
      case "error":
        return "text-panel-err";
      default:
        return "text-panel-fg";
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && status !== "running") onClose();
      }}
    >
      <div className="flex h-[85vh] w-full max-w-4xl flex-col rounded-lg border border-panel-border bg-panel-card shadow-2xl">
        <header className="flex items-start justify-between gap-3 border-b border-panel-border p-4">
          <div className="min-w-0 flex-1">
            <div className="text-xs text-panel-muted">Решение задачи #{task.id.slice(0, 8)}</div>
            <h2 className="truncate text-lg font-semibold">{task.title}</h2>
            {task.description && (
              <p className="mt-1 line-clamp-2 text-xs text-panel-muted">{task.description}</p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {status === "running" && <span className="pill pill-ok animate-pulse">● Работает</span>}
            {status === "done" && <span className="pill pill-ok">✅ Готово</span>}
            {status === "error" && <span className="pill pill-err">❌ Ошибка</span>}
            {status === "aborted" && <span className="pill">⏹ Прервано</span>}
          </div>
        </header>

        <div
          ref={scrollerRef}
          className="flex-1 overflow-y-auto bg-panel-bg p-3 font-mono text-xs"
        >
          {log.length === 0 && (
            <div className="text-panel-muted">Подключаюсь к Claude…</div>
          )}
          {log.map((entry) => (
            <div key={entry.id} className={`whitespace-pre-wrap break-words ${entryClass(entry.kind)}`}>
              <span className="text-[10px] text-panel-muted">[{entry.kind}] </span>
              {entry.text}
            </div>
          ))}
        </div>

        {status !== "running" && finalText && (
          <div className="border-t border-panel-border bg-panel-card p-3">
            <div className="mb-1 text-[10px] uppercase text-panel-muted">Финальный ответ</div>
            <div className="max-h-40 overflow-y-auto whitespace-pre-wrap text-sm">{finalText}</div>
            {reason && reason !== "ok" && (
              <div className="mt-2 text-xs text-panel-err">reason: {reason}</div>
            )}
          </div>
        )}

        <footer className="flex justify-end gap-2 border-t border-panel-border p-3">
          {status === "running" && (
            <button onClick={stop} className="btn text-sm border-panel-err text-panel-err hover:bg-panel-err hover:text-black">
              ⏹ Остановить
            </button>
          )}
          <button onClick={onClose} className="btn text-sm">
            {status === "running" ? "Закрыть" : "Готово"}
          </button>
        </footer>
      </div>
    </div>
  );
}
